import { useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const DJANGO_API = process.env.REACT_APP_API_DJANGO_URL;
export const SESSION_EXPIRED_EVENT = "session-expired";

export const clearSession = () => {
    [
        "accessToken", "refreshToken", "customer_id", "tenantId", "userId",
        "userName", "role", "session_token", "session_expires_at",
    ].forEach((k) => localStorage.removeItem(k));
};

// Calls the logout API and waits for the response (max 5s).
// Returns true only if the server confirmed the logout. Never throws.
export const logoutFromServer = async () => {
    const cloudToken = localStorage.getItem("accessToken");
    if (!cloudToken) return true; // nothing to revoke

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
        const res = await fetch(`${DJANGO_API}/api/logout/`, {
            method: "POST",
            headers: { Authorization: `Bearer ${cloudToken}` },
            signal: controller.signal,
        });
        return res.ok;
    } catch (err) {
        console.error("Logout API failed:", err);
        return false;
    } finally {
        clearTimeout(timeout);
    }
};

export default function SessionManager() {
    const navigate = useNavigate();
    const location = useLocation();
    const handlingRef = useRef(false);

    const triggerExpired = useCallback(async () => {
        if (handlingRef.current) return;
        if (!localStorage.getItem("session_token")) return; // not logged in
        handlingRef.current = true;

        // 1) call the logout API and wait for its response
        const ok = await logoutFromServer();
        if (!ok) console.warn("Server logout did not confirm; clearing locally anyway");

        // 2) clear local data and go to the login page (no message)
        clearSession();
        navigate("/", { replace: true, state: { sessionExpired: true } });

        handlingRef.current = false;
    }, [navigate]);

    const checkWithServer = useCallback(async () => {
        const token = localStorage.getItem("session_token");
        if (!token) return;
        try {
            const res = await fetch(`${DJANGO_API}/api/check_session/`, {
                headers: { "X-Session-Token": token },
            });
            if (res.status === 401) triggerExpired();
        } catch {
            /* network error: ignore, don't log out */
        }
    }, [triggerExpired]);

    // Exact timer from expires_at, re-armed on every route change
    useEffect(() => {
        const expiresAt = localStorage.getItem("session_expires_at");
        if (!expiresAt || !localStorage.getItem("session_token")) return;

        const ms = new Date(expiresAt).getTime() - Date.now();
        if (ms <= 0) {
            triggerExpired();
            return;
        }
        const timer = setTimeout(triggerExpired, ms);
        return () => clearTimeout(timer);
    }, [location.pathname, triggerExpired]);

    // Server polling, 401 event from API calls, and tab-focus re-check
    useEffect(() => {
        const interval = setInterval(checkWithServer, 30 * 1000);
        window.addEventListener(SESSION_EXPIRED_EVENT, triggerExpired);

        const onVisible = () => {
            if (document.visibilityState === "visible") checkWithServer();
        };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            clearInterval(interval);
            window.removeEventListener(SESSION_EXPIRED_EVENT, triggerExpired);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [triggerExpired, checkWithServer]);

    return null;
}